import type { DateTime } from "luxon";

/**
 * Non-negative modulo. Luxon day diffs can be negative (dates before the anchor), and JS `%`
 * preserves sign, so we normalize into `[0, m)` — the same idiom used by
 * `getWeekIndexOfSpecificDate` in `utils/dateUtils.ts`.
 */
export const nonNegativeMod = (n: number, m: number): number => ((n % m) + m) % m;

/**
 * Whole-day difference `a - b`. Both operands are first pinned to the SAME zone so their
 * `startOf("day")` instants align — otherwise `first_sunday` and `today`/cell dates in different
 * zones (observed: first_sunday in America/Los_Angeles, today in America/New_York) leave the
 * midnights hours apart, making `diff("days")` fractional (e.g. 2260.875) and floor to the wrong day
 * — the "cycle starts a day late" bug.
 */
const wholeDayDiff = (a: DateTime, b: DateTime, timezone: string): number =>
	Math.round(a.setZone(timezone).startOf("day").diff(b.setZone(timezone).startOf("day"), "days").days);

/**
 * Which cycle-day (0-based) a calendar date falls on, anchored to the department's `first_sunday`,
 * for a cycle of `cycleLength` days, evaluated in the department `timezone`. Drives the "Day N"
 * column label and today-highlight. Generalizes the weekly `get_master_schedule_week_index`
 * (backend `utils.py`) to a per-day cycle.
 */
export const getDayIndexInCycle = ({
	date,
	firstSunday,
	cycleLength,
	timezone,
}: {
	date: DateTime;
	firstSunday: DateTime;
	cycleLength: number;
	timezone: string;
}): number => nonNegativeMod(wholeDayDiff(date, firstSunday, timezone), cycleLength);

/**
 * The most recent cycle-aligned date on/before `today` (i.e. where the cycle-day index is 0) for a
 * cycle of `cycleLength` days, in the department `timezone`. This is the date shown in the first
 * "Week 1 / Day 1" cell. Column `(block b, day d)` maps to `cycleStartDate.plus({ days: b * cycleLength + d })`.
 */
export const getCycleStartDate = ({
	today,
	firstSunday,
	cycleLength,
	timezone,
}: {
	today: DateTime;
	firstSunday: DateTime;
	cycleLength: number;
	timezone: string;
}): DateTime => {
	const offset = getDayIndexInCycle({ date: today, firstSunday, cycleLength, timezone });
	return today.setZone(timezone).startOf("day").minus({ days: offset });
};

/**
 * Whether a consecutive-recurring shift is present on `date`. Mirrors the backend source of truth
 * `ConsecutiveDaysRecurring._calc_future_shifts_internal` (`backend/model.py`):
 * `days_diff % (working + rest) < working`, only for dates on/after the recurrence start.
 * Uses the SHIFT's own working/rest amounts (not the view constants) so non-4/2 consecutive
 * shifts still place correctly.
 */
export const isConsecutiveWorkingDay = ({
	date,
	recurrenceStart,
	workingDaysAmount,
	restDaysAmount,
	timezone,
}: {
	date: DateTime;
	recurrenceStart: DateTime;
	workingDaysAmount: number;
	restDaysAmount: number;
	timezone: string;
}): boolean => {
	const cycleLength = workingDaysAmount + restDaysAmount;
	if (cycleLength <= 0) return false;
	const daysDiff = wholeDayDiff(date, recurrenceStart, timezone);
	if (daysDiff < 0) return false;
	return daysDiff % cycleLength < workingDaysAmount;
};
